import React, { useState, useCallback, useContext, useEffect, useMemo } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "../../services/toast";


import { makeStyles } from 'tss-react/mui';
import { green, orange, red, blue, grey } from "@mui/material/colors";
import {
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Edit,
  CheckCircle,
  DeleteOutlined,
  Facebook,
  Instagram,
  ChatBubbleOutlineOutlined,
  RecordVoiceOverOutlined,
  PhoneInTalkOutlined,
  ScienceOutlined,
  WhatsApp,
  Add,
  SwapHoriz,
  ExpandMore,
  SearchOutlined,
  SmartToyOutlined,
  AccountTreeOutlined,
} from "@mui/icons-material";

import NewReleasesIcon from "@mui/icons-material/NewReleases";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import BlockIcon from "@mui/icons-material/Block";
import SignalCellular4BarIcon from "@mui/icons-material/SignalCellular4Bar";

import ConnectionPicker from "../../components/ConnectionPicker";
import SandboxModal from "../../components/SandboxModal";
import useEmbeddedSignup from "../../hooks/useEmbeddedSignup";
import WebChatModal from "../../components/WebChatModal";
import AvatarModal from "../../components/AvatarModal";
import SipCallModal from "../../components/SipCallModal";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

const STATUS_CONFIG = {
  CONNECTED:    { color: green[500],   label: i18n.t("connections.status.connected"),    bg: green[50]  },
  DISCONNECTED: { color: red[400],     label: i18n.t("connections.status.disconnected"), bg: red[50]    },
  PENDING:      { color: red[400],     label: i18n.t("connections.status.pending"),      bg: red[50]    },
  qrcode:       { color: blue[500],    label: i18n.t("connections.status.qrcode"),       bg: blue[50]   },
  OPENING:      { color: orange[500],  label: i18n.t("connections.status.opening"),      bg: orange[50] },
  TIMEOUT:      { color: orange[700],  label: i18n.t("connections.status.timeout"),      bg: orange[50] },
  PAIRING:      { color: orange[700],  label: i18n.t("connections.status.pairing"),      bg: orange[50] },
};

const STATE_CONFIG = {
  new:       { label: i18n.t("connections.state.new"),       icon: <NewReleasesIcon style={{ fontSize: 12 }} />, color: blue[600]   },
  warmingUp: { label: i18n.t("connections.state.warmingUp"), icon: <WhatshotIcon   style={{ fontSize: 12 }} />, color: orange[600] },
  asset:     { label: i18n.t("connections.state.asset"),     icon: <SignalCellular4BarIcon style={{ fontSize: 12 }} />, color: green[600] },
  rest:      { label: i18n.t("connections.state.rest"),      icon: <ReportProblemIcon style={{ fontSize: 12 }} />, color: "#9c27b0" },
  punished:  { label: i18n.t("connections.state.punished"),  icon: <BlockIcon      style={{ fontSize: 12 }} />, color: red[700]    },
};

const ROW_SEVERITY_CLASS = {
  CONNECTED: "rowConnected",
  DISCONNECTED: "rowAttention",
  PENDING: "rowAttention",
  qrcode: "rowWarning",
  OPENING: "rowWarning",
  TIMEOUT: "rowWarning",
  PAIRING: "rowWarning",
};

const CHANNEL_META = [
  { key: "whatsapp",       label: "WhatsApp",       icon: <WhatsApp style={{ color: "#25d366" }} />,  color: "#25d366", defaultOpen: false },
  { key: "whatsapp_cloud", label: "WhatsApp Cloud", icon: <WhatsApp style={{ color: "#25d3c1" }} />,  color: "#25d3c1", defaultOpen: false },
  { key: "sip",            label: "Llamadas",       icon: <PhoneInTalkOutlined style={{ color: "#2fae60" }} />,       color: "#2fae60", defaultOpen: false },
  { key: "facebook",       label: "Facebook",       icon: <Facebook style={{ color: "#3b5998" }} />,  color: "#3b5998", defaultOpen: false },
  { key: "instagram",      label: "Instagram",      icon: <Instagram style={{ color: "#e1306c" }} />, color: "#e1306c", defaultOpen: false },
  { key: "webchat",        label: "Web Chat",       icon: <ChatBubbleOutlineOutlined style={{ color: "#5b6cff" }} />, color: "#5b6cff", defaultOpen: false },
  { key: "avatar",         label: "Avatar",         icon: <RecordVoiceOverOutlined style={{ color: "#8a5cf6" }} />,   color: "#8a5cf6", defaultOpen: false },
  { key: "sandbox",        label: "Sandbox",        icon: <ScienceOutlined style={{ color: "#d98b2b" }} />,           color: "#d98b2b", defaultOpen: false },
];

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    background: "transparent",
    border: "none",
    boxShadow: "none",
    ...theme.scrollbarStyles,
  },

  accordion: {
    marginBottom: theme.spacing(1.5),
    borderRadius: "10px !important",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    "&:before": { display: "none" },
    "&.Mui-expanded": { margin: `0 0 ${theme.spacing(1.5)}` },
  },
  accordionSummary: {
    minHeight: 56,
    padding: theme.spacing(0, 2),
    "&.Mui-expanded": { minHeight: 56 },
    "& .MuiAccordionSummary-content": {
      alignItems: "center",
      gap: theme.spacing(1.5),
      margin: "12px 0",
    },
  },
  accordionDetails: {
    padding: 0,
    display: "block",
  },

  channelLabel: {
    fontWeight: 600,
    fontSize: "0.9rem",
    flex: 1,
  },
  summaryChip: {
    height: 20,
    fontSize: "0.68rem",
    "& .MuiChip-label": { padding: "0 7px" },
  },
  statusDots: {
    display: "flex",
    gap: 4,
    alignItems: "center",
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
  },

  tableScroll: {
    maxHeight: 344,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  table: {
    "& .MuiTableCell-root": {
      padding: "7px 14px",
      borderBottom: `1px solid ${theme.palette.divider}`,
      fontSize: "0.8rem",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      position: "sticky",
      top: 0,
      zIndex: 1,
      fontWeight: 600,
      fontSize: "0.72rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: theme.palette.text.secondary,
      background: theme.palette.mode === "light" ? "#f7f6f3" : "#242320",
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      background: theme.palette.action.hover,
    },
    "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
      borderBottom: "none",
    },
  },
  rowConnected: {
    boxShadow: `inset 3px 0 0 ${green[500]}`,
  },
  rowAttention: {
    boxShadow: `inset 3px 0 0 ${red[400]}`,
    background: theme.palette.mode === "light" ? "rgba(244,67,54,0.04)" : "rgba(244,67,54,0.07)",
  },
  rowWarning: {
    boxShadow: `inset 3px 0 0 ${orange[500]}`,
    background: theme.palette.mode === "light" ? "rgba(255,152,0,0.04)" : "rgba(255,152,0,0.06)",
  },
  channelSearch: {
    marginLeft: "auto",
    width: 180,
    "& .MuiOutlinedInput-root": { height: 30, fontSize: "0.78rem" },
  },
  searchEmpty: {
    padding: theme.spacing(2, 2),
    color: theme.palette.text.disabled,
    fontSize: "0.8rem",
    fontStyle: "italic",
    textAlign: "center",
  },

  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: "0.72rem",
    fontWeight: 500,
  },

  miniChip: {
    height: 20,
    fontSize: "0.68rem",
    "& .MuiChip-label": { paddingLeft: 5, paddingRight: 6 },
    "& .MuiChip-icon": { marginLeft: 5, fontSize: 11 },
  },

  rowActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    justifyContent: "flex-end",
  },
  iconBtn: {
    padding: 6,
    color: theme.palette.mode === "light" ? blue[700] : blue[300],
    backgroundColor: theme.palette.mode === "light" ? blue[50] : "rgba(100,181,246,0.12)",
    "&:hover": {
      color: theme.palette.mode === "light" ? blue[900] : blue[100],
      backgroundColor: theme.palette.mode === "light" ? blue[100] : "rgba(100,181,246,0.22)",
    },
  },
  iconBtnDanger: {
    color: theme.palette.mode === "light" ? red[700] : red[300],
    backgroundColor: theme.palette.mode === "light" ? red[50] : "rgba(239,83,80,0.12)",
    "&:hover": {
      color: theme.palette.mode === "light" ? red[900] : red[100],
      backgroundColor: theme.palette.mode === "light" ? red[100] : "rgba(239,83,80,0.22)",
    },
  },

  reconnectBtn: {
    fontSize: "0.7rem",
    fontWeight: 600,
    padding: "3px 12px",
    minWidth: 0,
    color: theme.palette.mode === "light" ? blue[800] : blue[100],
    backgroundColor: theme.palette.mode === "light" ? blue[50] : "rgba(100,181,246,0.16)",
    "&:hover": {
      backgroundColor: theme.palette.mode === "light" ? blue[100] : "rgba(100,181,246,0.28)",
    },
  },
  disconnectBtn: {
    fontSize: "0.7rem",
    fontWeight: 600,
    padding: "3px 12px",
    minWidth: 0,
    color: theme.palette.mode === "light" ? red[800] : red[100],
    backgroundColor: theme.palette.mode === "light" ? red[50] : "rgba(239,83,80,0.16)",
    "&:hover": {
      backgroundColor: theme.palette.mode === "light" ? red[100] : "rgba(239,83,80,0.28)",
    },
  },

  emptyChannel: {
    padding: theme.spacing(2, 2),
    color: theme.palette.text.disabled,
    fontSize: "0.8rem",
    fontStyle: "italic",
  },

  skeleton: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    height: 56,
    marginBottom: theme.spacing(1.5),
    background: theme.palette.action.hover,
    animation: "$pulse 1.5s ease-in-out infinite",
  },
  "@keyframes pulse": {
    "0%":   { opacity: 1   },
    "50%":  { opacity: 0.4 },
    "100%": { opacity: 1   },
  },
}));


const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: grey[400], label: status, bg: grey[50] };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 8px", borderRadius: 20,
      fontSize: "0.72rem", fontWeight: 500,
      color: cfg.color, background: cfg.bg,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

const ActionButton = ({ whatsApp, classes, onQr, onTryAgain, onDisconnect }) => {
  const s = whatsApp.status;
  if (s === "qrcode")
    return <Button size="small" variant="contained" color="primary" style={{ fontSize: "0.7rem", fontWeight: 600, padding: "3px 12px", minWidth: 0 }} onClick={() => onQr(whatsApp)}>QR</Button>;
  if (s === "DISCONNECTED" || s === "PENDING")
    return <Button size="small" className={classes.reconnectBtn} onClick={() => onTryAgain(whatsApp.id)}>{i18n.t("connections.buttons.reconnect")}</Button>;
  if (s === "CONNECTED" || s === "PAIRING" || s === "TIMEOUT")
    return <Button size="small" className={classes.disconnectBtn} onClick={() => onDisconnect(whatsApp.id)}>{i18n.t("connections.buttons.disconnect")}</Button>;
  if (s === "OPENING")
    return <Button size="small" variant="outlined" disabled style={{ fontSize: "0.7rem", padding: "3px 12px", minWidth: 0 }}><CircularProgress size={10} style={{ marginRight: 5 }} />{i18n.t("connections.buttons.connecting")}</Button>;
  return null;
};

const SEARCH_THRESHOLD = 5;

const ChannelSection = ({ meta, connections, classes, onEdit, onDelete, onQr, onTryAgain, onDisconnect, defaultOpen }) => {
  const [expanded, setExpanded] = useState(defaultOpen);
  const [filter, setFilter] = useState("");
  const connected = connections.filter(c => c.status === "CONNECTED").length;
  const showSearch = connections.length > SEARCH_THRESHOLD;

  const visibleConnections = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return connections;
    return connections.filter(
      c => (c.name || "").toLowerCase().includes(needle) || (c.number || "").includes(needle)
    );
  }, [connections, filter]);

  return (
    <Accordion className={classes.accordion} expanded={expanded} onChange={(_, v) => setExpanded(v)}>
      <AccordionSummary className={classes.accordionSummary} expandIcon={<ExpandMore />}>
        {}
        <span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {React.cloneElement(meta.icon, { style: { ...meta.icon.props.style, fontSize: 22 } })}
        </span>

        {}
        <Typography className={classes.channelLabel}>{meta.label}</Typography>

        {}
        <Box
          style={{ gap: 8 }}
          sx={{
            display: "flex",
            alignItems: "center"
          }}>
          <Chip
            className={classes.summaryChip}
            label={`${connections.length} ${connections.length === 1 ? i18n.t("connections.chips.lineSingular") : i18n.t("connections.chips.linePlural")}`}
            size="small"
            variant="outlined"
          />
          {connections.length > 0 && (
            <Chip
              className={classes.summaryChip}
              label={`${connected} ${connected !== 1 ? i18n.t("connections.chips.connectedPlural") : i18n.t("connections.chips.connectedSingular")}`}
              size="small"
              style={{
                borderColor: connected > 0 ? green[500] : red[400],
                color: connected > 0 ? green[600] : red[500],
              }}
              variant="outlined"
            />
          )}
          {}
          {connections.length > 0 && !expanded && (
            <div className={classes.statusDots}>
              {connections.map(c => {
                const cfg = STATUS_CONFIG[c.status] || { color: grey[400] };
                return <Tooltip key={c.id} title={`${c.name} — ${cfg.label || c.status}`}><div className={classes.dot} style={{ background: cfg.color }} /></Tooltip>;
              })}
            </div>
          )}

          {}
          {showSearch && expanded && (
            <TextField
              className={classes.channelSearch}
              size="small"
              variant="outlined"
              placeholder={i18n.t("connections.search.placeholder")}
              value={filter}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              onChange={(e) => setFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined style={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {connections.length === 0 ? (
          <div className={classes.emptyChannel}>{i18n.t("connections.emptyChannel")}</div>
        ) : visibleConnections.length === 0 ? (
          <div className={classes.searchEmpty}>{i18n.t("connections.search.noResults")}</div>
        ) : (
          <div className={classes.tableScroll}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("connections.table.nameNumber")}</TableCell>
                <TableCell>{i18n.t("connections.table.department")}</TableCell>
                <TableCell>{i18n.t("connections.table.automation")}</TableCell>
                <TableCell>{i18n.t("connections.table.state")}</TableCell>
                <TableCell>{i18n.t("connections.table.operational")}</TableCell>
                {meta.key === "whatsapp" && <TableCell align="center">{i18n.t("connections.table.credits")}</TableCell>}
                <TableCell>{i18n.t("connections.table.flags")}</TableCell>
                <TableCell align="right">{i18n.t("connections.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleConnections.map(w => {
                const stateCfg = STATE_CONFIG[w.state];
                const creditPct = w.credits ? (w.usageCredits / w.credits) * 100 : 0;
                const creditColor = creditPct < 50 ? green[600] : creditPct < 80 ? orange[600] : red[600];
                const severityClass = classes[ROW_SEVERITY_CLASS[w.status]] || "";
                const queues = w.queues || [];
                const hasBot = Boolean(w.promptId) || queues.some(q => q.promptId);
                const hasN8n = Boolean(w.integrationId) || queues.some(q => q.integrationId);
                return (
                  <TableRow key={w.id} className={severityClass}>
                    {}
                    <TableCell>
                      <Typography style={{ fontWeight: 600, fontSize: "0.82rem", lineHeight: 1.3 }}>{w.name}</Typography>
                      {w.number && <Typography style={{ fontSize: "0.72rem", color: grey[500] }}>{w.number}</Typography>}
                    </TableCell>

                    {}
                    <TableCell>
                      {queues.length ? (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {queues.map(q => (
                            <Chip
                              key={q.id}
                              className={classes.miniChip}
                              label={q.name}
                              size="small"
                              variant="outlined"
                              style={{ borderColor: q.color || grey[400], color: q.color || grey[700] }}
                            />
                          ))}
                        </div>
                      ) : "—"}
                    </TableCell>

                    {}
                    <TableCell>
                      <div style={{ display: "flex", gap: 4 }}>
                        {hasBot && (
                          <Tooltip title={i18n.t("connections.chips.botActive")}>
                            <Chip className={classes.miniChip} icon={<SmartToyOutlined style={{ fontSize: 11, color: blue[600] }} />} label="Bot" size="small" variant="outlined" style={{ borderColor: blue[400], color: blue[600] }} />
                          </Tooltip>
                        )}
                        {hasN8n && (
                          <Tooltip title={i18n.t("connections.chips.n8nActive")}>
                            <Chip className={classes.miniChip} icon={<AccountTreeOutlined style={{ fontSize: 11, color: "#ea580c" }} />} label="n8n" size="small" variant="outlined" style={{ borderColor: "#ea580c", color: "#ea580c" }} />
                          </Tooltip>
                        )}
                        {!hasBot && !hasN8n && "—"}
                      </div>
                    </TableCell>

                    {}
                    <TableCell><StatusBadge status={w.status} /></TableCell>

                    {}
                    <TableCell>
                      {stateCfg ? (
                        <Tooltip
                          title={
                            w.state === "punished" && w.reachoutLockUntil
                              ? `Restringida por WhatsApp hasta ${new Date(w.reachoutLockUntil).toLocaleString("es-ES")}`
                              : stateCfg.label
                          }
                        >
                          <Chip
                            className={classes.miniChip}
                            icon={React.cloneElement(stateCfg.icon, { style: { fontSize: 11, color: stateCfg.color } })}
                            label={stateCfg.label}
                            size="small"
                            variant="outlined"
                            style={{ borderColor: stateCfg.color, color: stateCfg.color }}
                          />
                        </Tooltip>
                      ) : "—"}
                    </TableCell>

                    {}
                    {meta.key === "whatsapp" && (
                      <TableCell align="center">
                        <span style={{ fontSize: "0.78rem", fontWeight: 600, color: creditColor }}>
                          {w.usageCredits} / {w.credits}
                        </span>
                      </TableCell>
                    )}

                    {}
                    <TableCell>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {w.isDefault && (
                          <Chip className={classes.miniChip} icon={<CheckCircle style={{ fontSize: 11, color: green[600] }} />} label={i18n.t("connections.chips.default")} size="small" variant="outlined" style={{ borderColor: green[500], color: green[600] }} />
                        )}
                        {w.isFallback && (
                          <Chip className={classes.miniChip} icon={<SwapHoriz style={{ fontSize: 11, color: blue[600] }} />} label={i18n.t("connections.chips.backup")} size="small" variant="outlined" style={{ borderColor: blue[400], color: blue[600] }} />
                        )}
                      </div>
                    </TableCell>

                    {}
                    <TableCell>
                      <div className={classes.rowActions}>
                        <ActionButton whatsApp={w} classes={classes} onQr={onQr} onTryAgain={onTryAgain} onDisconnect={onDisconnect} />
                        <Tooltip title={i18n.t("connections.rowActions.edit")}>
                          <IconButton className={classes.iconBtn} size="small" onClick={() => onEdit(w)}>
                            <Edit style={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.t("connections.rowActions.delete")}>
                          <IconButton className={`${classes.iconBtn} ${classes.iconBtnDanger}`} size="small" onClick={() => onDelete(w.id)}>
                            <DeleteOutlined style={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const Connections = () => {
  const { classes } = useStyles();
  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);

  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const confirmationModalInitialState = { action: "", title: "", message: "", whatsAppId: "" };
  const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);
  const [channelAvailability, setChannelAvailability] = useState({ avatar: true });

  useEffect(() => {
    if (!user?.companyId) return;
    api.get(`/companies/${user.companyId}`)
      .then(({ data }) => {
        setChannelAvailability({ avatar: data?.plan?.useAvatar !== false });
      })
      .catch(() => {});
  }, [user?.companyId]);

  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;
      api.post("/facebook", { facebookUserId: id, facebookUserToken: accessToken })
        .then(() => toast.success(i18n.t("connections.facebook.success")))
        .catch(toastError);
    }
  };
  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;
      api.post("/facebook", { addInstagram: true, facebookUserId: id, facebookUserToken: accessToken })
        .then(() => toast.success(i18n.t("connections.facebook.success")))
        .catch(toastError);
    }
  };
  const responseWhatsapp = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;
      api.post("/facebook", { addWhatsapp: true, facebookUserId: id, facebookUserToken: accessToken })
        .then(() => toast.success(i18n.t("connections.facebook.success")))
        .catch(toastError);
    }
  };

  const handleRequestNewQrCode = async (id) => { try { await api.put(`/whatsappsession/${id}`); } catch (err) { toastError(err); } };
  const [pickerOpen, setPickerOpen] = useState(false);
  const [webChatEdit, setWebChatEdit] = useState(null);
  const [webChatPestana, setWebChatPestana] = useState("chatbot");
  const [avatarEdit, setAvatarEdit] = useState(null);
  const [sipEdit, setSipEdit] = useState(null);

  const crearSandbox = async () => {
    try {
      const { data } = await api.post("/whatsapp/sandbox", {});
      setPickerOpen(false);
      setSandboxConexion(data);
    } catch (err) {
      toastError(err);
    }
  };

  const [sandboxConexion, setSandboxConexion] = useState(null);

  const crearWebChat = async () => {
    try {
      const { data } = await api.post("/whatsapp/webchat", {});
      setPickerOpen(false);

      setWebChatPestana("integracion");
      setWebChatEdit(data);
    } catch (err) {
      toastError(err);
    }
  };

  const crearAvatar = async () => {
    try {
      const { data } = await api.post("/whatsapp/avatar", {});
      setPickerOpen(false);
      setAvatarEdit(data);
    } catch (err) {
      toastError(err);
    }
  };

  const crearSip = async () => {
    try {
      const { data } = await api.post("/whatsapp/sip", {});
      setPickerOpen(false);
      setSipEdit(data);
    } catch (err) {
      toastError(err);
    }
  };

  const abrirCoexistencia = useEmbeddedSignup({
    onListo: async ({ code, wabaId, coexistencia }) => {
      if (!coexistencia) {
        toast.warning(
          "Completaste el alta normal de Cloud, no la de coexistencia. Vuelve a intentarlo y elige conectar tu cuenta existente de WhatsApp Business."
        );
      }

      try {
        await api.post("/whatsapp/coexistencia", { code, wabaId });
        setPickerOpen(false);
        toast.success("Conexión creada. El historial puede tardar unos minutos en aparecer.");
      } catch (err) {
        toastError(err);
      }
    },
    onError: (mensaje) => toast.warning(mensaje),
  });

  const handleOpenWhatsAppModal = () => { setSelectedWhatsApp(null); setWhatsAppModalOpen(true); };
  const handleCloseWhatsAppModal = useCallback(() => { setWhatsAppModalOpen(false); setSelectedWhatsApp(null); }, []);
  const handleOpenQrModal = (w) => { setSelectedWhatsApp(w); setQrModalOpen(true); };
  const handleCloseQrModal = useCallback(() => { setSelectedWhatsApp(null); setQrModalOpen(false); }, []);
  const handleEditWhatsApp = (w) => {
    if (w?.channel === "webchat") {
      setWebChatPestana("chatbot");
      setWebChatEdit(w);
      return;
    }

    if (w?.channel === "sandbox") {
      setSandboxConexion(w);
      return;
    }

    if (w?.channel === "avatar") {
      setAvatarEdit(w);
      return;
    }

    if (w?.channel === "sip") {
      setSipEdit(w);
      return;
    }

    setSelectedWhatsApp(w);
    setWhatsAppModalOpen(true);
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    setConfirmModalInfo(
      action === "disconnect"
        ? { action, title: i18n.t("connections.confirmationModal.disconnectTitle"), message: i18n.t("connections.confirmationModal.disconnectMessage"), whatsAppId }
        : { action, title: i18n.t("connections.confirmationModal.deleteTitle"),     message: i18n.t("connections.confirmationModal.deleteMessage"),     whatsAppId }
    );
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try { await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`); } catch (err) { toastError(err); }
    }
    if (confirmModalInfo.action === "delete") {
      try { await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`); toast.success(i18n.t("connections.toasts.deleted")); } catch (err) { toastError(err); }
    }
    setConfirmModalInfo(confirmationModalInitialState);
  };

  const byChannel = (key) => (whatsApps || []).filter(w => w.channel === key);

  return (
    <MainContainer maxWidth={false}>
      <ConfirmationModal title={confirmModalInfo.title} open={confirmModalOpen} onClose={setConfirmModalOpen} onConfirm={handleSubmitConfirmationModal}>
        {confirmModalInfo.message}
      </ConfirmationModal>
      <QrcodeModal open={qrModalOpen} onClose={handleCloseQrModal} whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id} />
      <WhatsAppModal open={whatsAppModalOpen} onClose={handleCloseWhatsAppModal} whatsAppId={!qrModalOpen && selectedWhatsApp?.id} />

      {}
      <ConnectionPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onWhatsApp={handleOpenWhatsAppModal}
        onFacebook={responseFacebook}
        onInstagram={responseInstagram}
        onWhatsAppCloud={responseWhatsapp}
        onCoexistencia={abrirCoexistencia}
        onWebChat={crearWebChat}
        onSandbox={crearSandbox}
        onAvatar={crearAvatar}
        channelAvailability={channelAvailability}
        onSip={crearSip}
      />

      {}
      <SandboxModal
        open={Boolean(sandboxConexion)}
        onClose={() => setSandboxConexion(null)}
        whatsapp={sandboxConexion}
      />

      {}
      <WebChatModal
        open={Boolean(webChatEdit)}
        conexion={webChatEdit}
        pestanaInicial={webChatPestana}
        backendUrl={process.env.REACT_APP_BACKEND_URL || window.location.origin}
        onClose={() => setWebChatEdit(null)}
      />

      <AvatarModal
        open={Boolean(avatarEdit)}
        conexion={avatarEdit}
        backendUrl={process.env.REACT_APP_BACKEND_URL || window.location.origin}
        onClose={() => setAvatarEdit(null)}
      />

      <SipCallModal
        open={Boolean(sipEdit)}
        conexion={sipEdit}
        backendUrl={process.env.REACT_APP_BACKEND_URL || window.location.origin}
        onClose={() => setSipEdit(null)}
      />

      <MainHeader>
        <Title>{i18n.t("connections.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setPickerOpen(true)}
          >
            {i18n.t("connections.buttons.newConnection")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {loading ? (
          [1, 2, 3, 4].map(n => <div key={n} className={classes.skeleton} />)
        ) : (() => {
          const visibles = CHANNEL_META.filter(meta => byChannel(meta.key).length > 0);

          if (!visibles.length) {
            return (
              <Typography className={classes.emptyChannel}>
                Todavía no hay ninguna conexión. Creá una con "Nueva Conexión".
              </Typography>
            );
          }

          return visibles.map(meta => (
            <ChannelSection
              key={meta.key}
              meta={meta}
              connections={byChannel(meta.key)}
              classes={classes}
              defaultOpen={meta.defaultOpen}
              onEdit={handleEditWhatsApp}
              onDelete={(id) => handleOpenConfirmationModal("delete", id)}
              onQr={handleOpenQrModal}
              onTryAgain={handleRequestNewQrCode}
              onDisconnect={(id) => handleOpenConfirmationModal("disconnect", id)}
            />
          ));
        })()}
      </Paper>
    </MainContainer>
  );
};

export default Connections;
